/*
 * Purpose: AI-composed mission request page — schema-driven field library
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { ClipboardList, LayoutTemplate } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { MissionKnowledgeRequest } from '@/services/mission-knowledge/types';
import { requestIsComplete } from '@/lib/missionRequestFields';
import { useMissionKnowledgeStore } from '@/stores/missionKnowledgeStore';
import { MissionRequestField } from '@/components/mission/request-fields/MissionRequestField';
import { cn } from '@/lib/utils';

interface MissionRequestPageProps {
  requests: MissionKnowledgeRequest[];
  className?: string;
}

export function MissionRequestPage({ requests, className }: MissionRequestPageProps) {
  if (requests.length === 0) return null;

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-start gap-2">
        <LayoutTemplate className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground">Request fields for this Mission</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            AutoBlocks composed these from your outcome using the shared field library — answers stay on this Mission.
          </p>
        </div>
      </div>

      {requests.map((req) => (
        <MissionRequestSection key={req.id} request={req} />
      ))}
    </div>
  );
}

function MissionRequestSection({ request }: { request: MissionKnowledgeRequest }) {
  const structuredAnswers = useMissionKnowledgeStore((s) => s.structuredAnswers);
  const complete = requestIsComplete(request, structuredAnswers);
  const isCallout = request.layout === 'callout';

  return (
    <fieldset
      className={cn(
        'rounded-md border p-3 space-y-3',
        isCallout ? 'border-border/40 bg-muted/20' : 'border-border/60 bg-background/80'
      )}
    >
      <legend className="flex flex-wrap items-center gap-2 text-xs font-medium px-1">
        {!isCallout && <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />}
        {request.title}
        {complete ? (
          <Badge variant="secondary" className="text-[10px]">
            Complete
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-400">
            Needed
          </Badge>
        )}
      </legend>
      {request.description && (
        <p className="text-[11px] text-muted-foreground -mt-1">{request.description}</p>
      )}
      {request.fields.map((field) => (
        <MissionRequestField key={field.id} field={field} requestId={request.id} />
      ))}
    </fieldset>
  );
}
